import type {
  CardBrand,
  Payment,
  PaymentMethod,
  PaymentMethodType,
  PaymentMethodWithPolicies,
  PaymentStatus,
  PolicyPaymentMethod,
  Transaction,
} from './types';

export function mapCardBrand(brand: string): CardBrand {
  switch (brand.toLowerCase()) {
    case 'visa':
      return 'visa';
    case 'mastercard':
      return 'mastercard';
    case 'amex':
    case 'american express':
      return 'amex';
    case 'discover':
      return 'discover';
    default:
      return 'unknown';
  }
}

export function mapPaymentStatus(status: Transaction['status']): PaymentStatus {
  switch (status) {
    case 'Succeeded':
      return 'completed';
    case 'Pending':
      return 'processing';
    case 'Failed':
      return 'failed';
    case 'Unknown':
      return 'unknown';
  }
}

function mapTransactionPaymentMethodType(
  paymentMethodType: Transaction['paymentMethodType'],
  last4: string | null,
  network: string | null
): PaymentMethodType {
  switch (paymentMethodType) {
    case 'Card':
      return { kind: 'card', brand: mapCardBrand(network ?? ''), last4: last4 ?? '' };
    case 'Bank':
      return { kind: 'ach', last4: last4 ?? '' };
    case 'Unknown':
      return { kind: 'unknown' };
  }
}

export function mapTransactionToPayment(tx: Transaction, index: number): Payment {
  return {
    paymentId: String(index),
    date: tx.timestamp.substring(0, 10),
    policyNumber: tx.policyNumber ?? 'Unattributed',
    policyId: tx.policyId,
    method: mapTransactionPaymentMethodType(tx.paymentMethodType, tx.last4, tx.network),
    status: mapPaymentStatus(tx.status),
    amount: tx.transactionType === 'Refund' ? -tx.amount : tx.amount,
  };
}

function mapPaymentMethodItemType(item: PaymentMethodWithPolicies): PaymentMethodType {
  switch (item.type) {
    case 'Card':
      return {
        kind: 'card',
        brand: mapCardBrand(item.card.brand),
        last4: item.card.last4,
        expirationMonth: item.card.expirationMonth,
        expirationYear: item.card.expirationYear,
      };
    case 'Bank':
      return { kind: 'ach', last4: item.bank.last4 ?? '' };
    case 'Unknown':
      return { kind: 'unknown' };
    case 'None':
      return { kind: 'missing' };
  }
}

// Policy numbers embed the term as a trailing suffix (`-000`, `-001`, ...),
// so a lexicographic descending sort places the latest term first — matching
// design's newest-to-oldest ordering.
const byNewestPolicyTermFirst = (
  a: { policyNumber: string },
  b: { policyNumber: string }
): number => b.policyNumber.localeCompare(a.policyNumber);

export function mapPaymentMethodItemToRow(item: PaymentMethodWithPolicies): PaymentMethod {
  return {
    method: mapPaymentMethodItemType(item),
    policies: item.policies
      .map(({ policyId, policyNumber }) => ({ policyId, policyNumber }))
      .sort(byNewestPolicyTermFirst),
  };
}

export function mapPolicyPaymentMethodType(response: PolicyPaymentMethod): PaymentMethodType {
  switch (response.type) {
    case 'Card':
      return {
        kind: 'card',
        brand: mapCardBrand(response.card.brand),
        last4: response.card.last4,
        expirationMonth: response.card.expirationMonth,
        expirationYear: response.card.expirationYear,
      };
    case 'Bank':
      return { kind: 'ach', last4: response.bank.last4 ?? '' };
    case 'Unknown':
      return { kind: 'unknown' };
    case 'None':
      return { kind: 'missing' };
  }
}
